<script setup>
import { computed, nextTick, onUnmounted, ref, useId, watch } from 'vue';
import { t } from '../i18n';
import { isPhone } from '../isPhone';

// Bottom sheet on a phone, a centred dialog from sm (docs/design/SPEC.md, «Нижний лист»).
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close']);

const titleId = useId();
const panel = ref(null);
const heading = ref(null);
let returnFocusTo = null;

function close() {
  emit('close');
}

function focusables() {
  return panel.value
    ? [...panel.value.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')]
    : [];
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    event.preventDefault();
    close();
    return;
  }
  if (event.key !== 'Tab') {
    return;
  }
  // Focus stays inside the sheet.
  const items = focusables();
  if (items.length === 0) {
    event.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

// Swipe down to close on a phone (docs/design/SPEC.md, «Нижний лист»): from the top of the sheet's own
// scroll, the sheet follows the finger; past 100 px or a quick flick it closes, otherwise it springs back.
const CLOSE_DISTANCE = 100;
const CLOSE_SPEED = 0.5;
const drag = ref(0);
const dragging = ref(false);
let dragStart = null;
let dragStartTime = 0;

function onTouchStart(event) {
  dragStart = isPhone.value && (panel.value?.scrollTop ?? 0) <= 0 ? event.touches[0].clientY : null;
  dragStartTime = Date.now();
}

function onTouchMove(event) {
  if (dragStart === null) {
    return;
  }
  const dy = event.touches[0].clientY - dragStart;
  if (dy <= 0 && !dragging.value) {
    // Scrolling up inside the sheet: not a close gesture.
    dragStart = null;
    return;
  }
  dragging.value = true;
  drag.value = Math.max(0, dy);
  event.preventDefault();
}

function onTouchEnd() {
  if (!dragging.value) {
    dragStart = null;
    return;
  }
  const speed = drag.value / Math.max(Date.now() - dragStartTime, 1);
  dragging.value = false;
  dragStart = null;
  if (drag.value > CLOSE_DISTANCE || speed > CLOSE_SPEED) {
    // Slides on down from where the finger left it, then the sheet is gone.
    drag.value = window.innerHeight;
    close();
  } else {
    drag.value = 0;
  }
}

const panelStyle = computed(() => (drag.value > 0
  ? { transform: `translateY(${drag.value}px)`, transition: dragging.value ? 'none' : 'transform 200ms ease-out' }
  : undefined));

function lockScroll(locked) {
  document.documentElement.style.overflow = locked ? 'hidden' : '';
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      drag.value = 0;
      returnFocusTo = document.activeElement;
      lockScroll(true);
      await nextTick();
      heading.value?.focus();
    } else {
      lockScroll(false);
      returnFocusTo?.focus?.();
      returnFocusTo = null;
    }
  }
);

onUnmounted(() => lockScroll(false));
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200 motion-reduce:transition-none"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[90] bg-black/60"
        aria-hidden="true"
        @click="close"
      />
    </Transition>
    <Transition
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="max-sm:translate-y-full sm:opacity-0"
      leave-active-class="transition duration-200 ease-in motion-reduce:transition-none"
      leave-to-class="max-sm:translate-y-full sm:opacity-0"
    >
      <div
        v-if="open"
        ref="panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        class="fixed inset-x-0 bottom-0 z-[95] max-h-[88dvh] overflow-y-auto overscroll-contain rounded-t-3xl bg-slate-900 pb-[max(env(safe-area-inset-bottom),16px)] text-slate-100 sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border sm:border-slate-700 sm:pb-4"
        :style="panelStyle"
        @keydown="onKeydown"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @touchcancel="onTouchEnd"
      >
        <div
          class="mx-auto mt-2 h-1 w-9 rounded-full bg-slate-600 sm:hidden"
          aria-hidden="true"
        />
        <div class="flex h-12 items-center justify-between gap-3 pl-5 pr-2">
          <h2
            :id="titleId"
            ref="heading"
            tabindex="-1"
            class="min-w-0 truncate text-[1.0625rem] font-semibold focus:outline-none"
          >
            {{ title }}
          </h2>
          <button
            type="button"
            class="flex h-11 shrink-0 items-center rounded-lg px-3 text-[0.9375rem] text-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            @click="close"
          >
            {{ t('common.close') }}
          </button>
        </div>
        <slot />
      </div>
    </Transition>
  </Teleport>
</template>
